import { initializeApp } from 'firebase/app'
import { initializeFirestore, writeBatch, doc, runTransaction, deleteField } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getAuth } from 'firebase/auth'
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'

const config = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	databaseURL: `https://${process.env.REACT_APP_FIREBASE_PROJECT_ID}.firebaseio.com`,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.appspot.com`,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSENGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

// Each dev registers the debug token printed in the browser console in Firebase App Check.
// The NODE_ENV guard strips this block from production builds.
if (process.env.NODE_ENV === 'development') {
	window.FIREBASE_APPCHECK_DEBUG_TOKEN = true
}

export const app = initializeApp(config)

initializeAppCheck(app, {
	provider: new ReCaptchaV3Provider('6LcIVtccAAAAAG79NGEE_Rv-Gh82dXAig3j9vbLz'),
	isTokenAutoRefreshEnabled: true,
})

export const firestore = initializeFirestore(app, { ignoreUndefinedProperties: true })
export const storage = getStorage(app)
export const runFirestoreTransaction = runTransaction
export const createFirestoreRef = (collection, id) => doc(firestore, collection, id)
export const auth = getAuth(app)

const functions = getFunctions(app)
const functionsV2 = getFunctions(app, 'europe-west1')

// connectFunctionsEmulator(functionsV2, '127.0.0.1', 5001)

export const httpDomain = 'https://keoydqnp9l.execute-api.eu-west-3.amazonaws.com/dev/'

export const msConfig = {
	appId: '3b3739d2-25f9-4599-bb95-c251ac8b15bd',
}

export const createBatch = () => {
	return writeBatch(firestore)
}

export const getCallableFunction = (functionName, options = undefined) => {
	return httpsCallable(functions, functionName, options)
}

export const getCallableFunctionV2 = (functionName, options = undefined) => {
	return httpsCallable(functionsV2, functionName, options)
}

// Stamps the current user's uid on a Firestore payload so server-side triggers
// (activity log) can attribute the change. The stamp always wins over a stale
// value copied from a source doc (duplications). `create` is reserved for
// brand-new ids: a merge-set on an existing doc must not rewrite `createdBy`.
// Without a signed-in user (before auth restore, after sign-out) the payload is
// left untouched.
export const withActor = (data, { create = false } = {}) => {
	const uid = auth.currentUser?.uid
	if (!uid) return data
	return create ? { ...data, createdBy: uid, updatedBy: uid } : { ...data, updatedBy: uid }
}

// Champs réservés au serveur sur les fiches compte et contact (tiers Sage,
// BS-1765) : `metadata` (sac d'intégration exposé par l'API) et `sageSync`
// (trace du dernier envoi). Les formulaires les chargent avec la fiche ; ils
// ne doivent jamais repartir en écriture, sinon une copie périmée écraserait
// la synchro et les rules qui gèlent ces champs rejetteraient toute la sauvegarde.
// reservations.calendarExports : événements d'agendas externes exportés depuis la v2, écrits par les Cloud Functions seulement (gelé dans les rules)
// reservations.pi : trace de la table ouverte sur la caisse PI Electronique, écrite par le callable pi_openTable seulement (gelé dans les rules)
const SERVER_OWNED_FIELDS = { accounts: ['metadata', 'sageSync'], clients: ['metadata', 'sageSync'], reservations: ['calendarExports', 'pi'] }

export const stripServerOwnedFields = (collection, data) => {
	const fields = SERVER_OWNED_FIELDS[collection]
	if (!fields || !data || typeof data !== 'object') return data
	const copy = { ...data }
	fields.forEach(field => delete copy[field])
	return copy
}

// A cleared end time (TimeSelect allowClear stores null) removes the field
// instead of storing null: the end time is optional (BS-2549)
export const dropClearedEndTime = (collection, data) =>
	collection === 'reservations' && data?.heureFin === null ? { ...data, heureFin: deleteField() } : data

export const handleBatch = (batch, collection, id, data, type = 'update') => {
	const payload = withActor(dropClearedEndTime(collection, stripServerOwnedFields(collection, data)))
	if (type === 'update') {
		batch.update(doc(firestore, collection, id), payload)
	} else if (type === 'set') {
		batch.set(doc(firestore, collection, id), payload, { merge: true })
	}
}
